import styled, { keyframes } from 'styled-components';
import { Text } from '~/components/ui/Text';
import { FishRarity } from '~/shared';
import bg from '../../assets/images/image_bg.png';

const StyledWrapper = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(17, 19, 20, 0.5);

	transition: opacity 0.3s ease;
	opacity: 0;

	&.modal-enter {
		opacity: 0;
	}

	&.modal-enter-active,
	&.modal-enter-done,
	&.modal-exit {
		opacity: 1;
	}

	&.modal-exit-active {
		opacity: 0;
	}
`;

const StyledContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 500px;
	background: #111314;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	padding: 29px;
`;

const StyledTitle = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
	}

	${Text} {
		font-weight: 500;
		font-size: 18px;
		color: #ffffff;
	}
`;

const StyledTagsContainer = styled.div`
	display: flex;
	gap: 10px;
`;

const StyledImageContainer = styled.div`
	position: relative;
	height: 260px;
	display: flex;
	justify-content: space-between;
	padding: 20px;
	background: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(255, 255, 255, 0.05);
	border-radius: 10px;
	background-image: url(${bg});
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;

	& > ${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;
	}

	& > img {
		position: absolute;
		top: 70px;
		left: 50%;
		transform: translateX(-50%);
		width: 300px;
		height: 140px;
		object-fit: contain;
		user-select: none;
		pointer-events: none;
	}
`;

const StyledTag = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	height: 20px;
	padding: 0 5px;
	border-radius: 5px;
	overflow: hidden;

	${Text} {
		font-weight: 500;
		font-size: 12px;
	}

	&.${FishRarity.COMMON} {
		background: rgba(255, 255, 255, 0.1);
		border-right: 2px solid rgba(255, 255, 255, 0.1);
		color: #ffffff;
	}

	&.${FishRarity.TROPHY} {
		background: #2d70d9;
		border-right: 2px solid #458dff;
		color: #ffffff;
	}

	&.${FishRarity.RARE} {
		background: #fabe2c;
		border-right: 2px solid #ffe9b2;
		color: #000000;
	}

	&.${FishRarity.FORBIDDEN} {
		background: #c71e1e;
		border-right: 2px solid #ff6363;
		color: #ffffff;
	}
`;

const shineAnimation = keyframes`
	0% {
		left: -10px;
	}
	66.67% {
		left: 100%;
	}
	100% {
		left: 100%;
	}
`;

const StyledShine = styled.div`
	position: absolute;
	width: 20px;
	height: 20px;
	left: -10px;
	top: 0;
	z-index: -1;
	background: linear-gradient(
		270deg,
		rgba(255, 255, 255, 0) 0%,
		#ffffff 50.48%,
		rgba(255, 255, 255, 0) 100%
	);
	opacity: 0.25;
	animation: ${shineAnimation} 3s ease infinite;
`;

const StyledProgressWrapper = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: 100px 1fr 100px;
	align-items: center;
	padding: 0 20px;
	height: 50px;
	background: rgba(255, 255, 255, 0.02);
	border: 1px solid rgba(255, 255, 255, 0.05);
	border-radius: 10px;

	& > ${Text} {
		font-size: 14px;
		color: #ffffff;
		opacity: 0.5;
	}
`;

const StyledProgressXPContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	img {
		position: relative;
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
	}

	${Text} {
		position: relative;
		font-weight: 400;
		font-size: 12px;
		color: rgba(255, 255, 255, 0.5);

		span {
			font-size: 14px;
			font-weight: 700;
			color: #ffffff;
		}
	}
`;

const StyledProgressContainer = styled.div`
	position: absolute;
	top: 4px;
	left: 4px;
	right: 4px;
	bottom: 4px;
`;

const StyledProgress = styled.div`
	height: 100%;
	background: #2d70d9;
	border-radius: 5px;
`;

const StyledButtonsContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
`;

const StyledButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
`;

const StyledExpContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	height: 20px;
	padding: 0 5px;
	border-radius: 5px;
	background-color: #2d70d9;

	img {
		width: 10px;
		height: 10px;
		user-select: none;
		pointer-events: none;
	}

	${Text} {
		font-weight: 500;
		font-size: 12px;
		color: #ffffff;
	}
`;

const StyledWeightContainer = styled.div`
	position: absolute;
	bottom: 20px;
	left: 20px;
	display: flex;
	align-items: center;
	gap: 10px;

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
	}

	${Text} {
		font-weight: 500;
		font-size: 16px;
		color: #ffffff;

		span {
			font-weight: 400;
			opacity: 0.5;
		}
	}
`;

const StyledHintButtonContainer = styled.div`
	position: absolute;
	bottom: 20px;
	right: 20px;

	button {
		width: 20px;
	}
`;

const StyledHint = styled.div`
	position: absolute;
	bottom: 50px;
	right: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 285px;
	padding: 20px;
	border-radius: 10px;
	background-color: rgba(255, 255, 255, 0.9);

	opacity: 0;
	transition: opacity 0.3s ease;

	&.hint-enter {
		opacity: 0;
	}

	&.hint-enter-active,
	&.hint-enter-done,
	&.hint-exit {
		opacity: 1;
	}

	&.hint-exit-active {
		opacity: 0;
	}

	${Text} {
		font-size: 14px;
		color: #000000;

		&:nth-child(1) {
			font-weight: 500;
		}

		&:nth-child(2) {
			opacity: 0.5;
		}
	}
`;

export {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledExpContainer,
	StyledHint,
	StyledHintButtonContainer,
	StyledImageContainer,
	StyledProgress,
	StyledProgressContainer,
	StyledProgressWrapper,
	StyledProgressXPContainer,
	StyledShine,
	StyledTag,
	StyledTagsContainer,
	StyledTitle,
	StyledWeightContainer,
	StyledWrapper,
};
